// Package imports:
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import Link from '../../../ui-elements/Link/Link';
import VerticalTable from '../../../ui-elements/Table/VerticalTable';
import Table from '../../../ui-elements/Table/Table';
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
// Type imports:
import { IEventReport } from '../../../types/KeldanTypes';
import { ConstructionsSearchResult, ConstructionsSearchResultOld } from '../../../types/VehicleTypes';

interface IProps {
    eventId?: string,
    data?: IEventReport<ConstructionsSearchResult>,
    dataOld?: IEventReport<ConstructionsSearchResultOld>,
    isExample?: boolean
}

const ConstructionReportBody: React.FC<IProps> = ({
    eventId,
    data,
    dataOld,
    isExample = false
}) => {
    return data
        ? <DisplayBox grayBackground={isExample}>
            {/* Grunnupplýsingar */}
            <div className='basic-info-header-wrapper'>
                <h4>Grunnupplýsingar</h4>
                <div className='pdf-link-wrapper'>
                    <span>
                        <FontAwesomeIcon icon={faFilePdf} className="icon-fa" />
                        <Link
                            targetBlank
                            linkSize='15'
                            url={`/Pdf/Construction/${eventId}`}
                            disabled={isExample}
                        >
                            Sækja skýrslu
                        </Link>
                    </span>
                    <span className='italic-text'>{`Skýrsla keypt: ${data?.eventDate  ?? ''}`} </span>
                </div>
            </div>
            <div className='vertical-table-wrapper'>
                <VerticalTable
                    data={[data.overview.data]}
                    columns={[{
                        title: 'Skráð',
                        dataAccessor: ({regDate}) => `${regDate.dateTime.date.day}.${regDate.dateTime.date.month}.${regDate.dateTime.date.year}`
                    }, {
                        title: 'Gerð',
                        dataAccessor: ({make}) => make,
                    }, {
                        title: 'Undirflokkur',
                        dataAccessor: ({subcategory}) => subcategory,
                    }, {
                        title: 'Eigandi',
                        dataAccessor: ({owner}) => owner,
                    }, {
                        title: 'Umráðamaður',
                        dataAccessor: ({caretaker}) => caretaker === '' ? 'Á ekki við' : caretaker,
                    }, {
                        title: 'Innflytjandi',
                        dataAccessor: ({importer}) => importer,
                    }, {
                        title: 'Framleiðslunúmer',
                        dataAccessor: ({vin}) => vin,
                    }
                ]}
                    tableSize="lg"
                />
                <VerticalTable
                    data={[data.overview.data]}
                    columns={[{
                        title: 'Tegund',
                        dataAccessor: ({type}) => type,
                    }, {
                        title: 'Yfirflokkur',
                        dataAccessor: ({category}) => category,
                    }, {
                        title: 'Árgerð',
                        dataAccessor: ({model}) => model,
                    }, {
                        title: 'Kennitala eiganda',
                        dataAccessor: ({ownerSsn}) => ownerSsn,
                    }, {
                        title: 'Kennitala umráðamanns',
                        dataAccessor: ({caretakerSsn}) => caretakerSsn === '' ? 'Á ekki við' : caretakerSsn,
                    }, {
                        title: 'Staða',
                        dataAccessor: ({status}) => status,
                    }, {
                        title: 'Framleiðsluland	',
                        dataAccessor: ({country}) => country,
                    }]}
                    tableSize="lg"
                />
            </div>
            {
                data.overview.data.ownedHistory !== null &&
                    <div>
                        <h4>Eigendasaga</h4>
                        <Table 
                            data={data.overview.data.ownedHistory}
                            columns={[{
                                title: 'Eigandi',
                                dataAccessor: ({name}) => name,
                                textAlign: 'left',
                            }, {
                                title: 'Kennitala',
                                dataAccessor: ({ssn}) => ssn,
                                textAlign: 'left',
                            }, {
                                title: 'Skráningarlykill',
                                dataAccessor: ({code}) => code,
                            }, {
                                title: 'Dags. eigendaskipta',
                                dataAccessor: ({dateTraded}) => `${dateTraded?.dateTime.date.day}.${dateTraded?.dateTime.date.month}.${dateTraded?.dateTime.date.year}`,
                            }, {
                                title: 'Dags. skráningar',
                                dataAccessor: ({dateRegistered}) => `${dateRegistered?.dateTime.date.day}.${dateRegistered?.dateTime.date.month}.${dateRegistered?.dateTime.date.year}`,
                            }]}
                            tableSize='lg'
                        />
                    </div>

            }
        </DisplayBox>
        : dataOld
        ? <DisplayBox>
            {/* Grunnupplýsingar */}
            <div className='basic-info-header-wrapper'>
                <h4>Grunnupplýsingar</h4>
                <div className='pdf-link-wrapper'>
                    <span>
                        <FontAwesomeIcon icon={faFilePdf} className="icon-fa" />
                        <Link targetBlank linkSize='15' url={`/Pdf/Construction/${eventId}`}>Sækja skýrslu</Link>
                    </span>
                    <span className='italic-text'>{`Skýrsla keypt: ${dataOld.eventDate ?? ''}`} </span>
                </div>
            </div>
            <div className='vertical-table-wrapper'>
                <VerticalTable
                    data={[dataOld.overview.data]}
                    columns={[{
                        title: 'Skráð',
                        dataAccessor: ({regDate}) => `${regDate.day}.${regDate.month}.${regDate.year}`
                    }, {
                        title: 'Gerð',
                        dataAccessor: ({make}) => make,
                    }, {
                        title: 'Undirflokkur',
                        dataAccessor: ({subcategory}) => subcategory,
                    }, {
                        title: 'Eigandi',
                        dataAccessor: ({owner}) => owner,
                    }, {
                        title: 'Umráðamaður',
                        dataAccessor: ({caretaker}) => caretaker === '' ? 'Á ekki við' : caretaker,
                    }, {
                        title: 'Innflytjandi',
                        dataAccessor: ({importer}) => importer,
                    }, {
                        title: 'Framleiðslunúmer',
                        dataAccessor: ({vin}) => vin,
                    }
                ]}
                    tableSize="lg"
                />
                <VerticalTable
                    data={[dataOld.overview.data]}
                    columns={[{
                        title: 'Tegund',
                        dataAccessor: ({type}) => type,
                    }, {
                        title: 'Yfirflokkur',
                        dataAccessor: ({category}) => category,
                    }, {
                        title: 'Árgerð',
                        dataAccessor: ({model}) => model,
                    }, {
                        title: 'Kennitala eiganda',
                        dataAccessor: ({ownerSsn}) => ownerSsn,
                    }, {
                        title: 'Kennitala umráðamanns',
                        dataAccessor: ({caretakerSsn}) => caretakerSsn === '' ? 'Á ekki við' : caretakerSsn,
                    }, {
                        title: 'Staða',
                        dataAccessor: ({status}) => status,
                    }, {
                        title: 'Framleiðsluland	',
                        dataAccessor: ({country}) => country,
                    }]}
                    tableSize="lg"
                />
            </div>
            {
                dataOld.overview.data.ownedHistory.anyType !== null &&
                    <div>
                        <h4>Eigendasaga</h4>
                        <Table 
                            data={dataOld.overview.data.ownedHistory.anyType}
                            columns={[{
                                title: 'Eigandi',
                                dataAccessor: ({name}) => name,
                                textAlign: 'left',
                            }, {
                                title: 'Kennitala',
                                dataAccessor: ({ssn}) => ssn,
                                textAlign: 'left',
                            }, {
                                title: 'Skráningarlykill',
                                dataAccessor: ({code}) => code,
                            }, {
                                title: 'Dags. eigendaskipta',
                                dataAccessor: ({dateTraded}) => `${dateTraded.day}.${dateTraded.month}.${dateTraded.year}`,
                            }, {
                                title: 'Dags. skráningar',
                                dataAccessor: ({dateRegistered}) => `${dateRegistered.day}.${dateRegistered.month}.${dateRegistered.year}`,
                            }]}
                            tableSize='lg'
                        />
                    </div>
            }
        </DisplayBox>
        : null
    ;
}

export default ConstructionReportBody;